import { useEffect, useRef } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { Task, TaskDevice, TaskDeviceStatus, StagedConfig, ListResponse, SingleResponse } from "./types";

// The artefact a task rolls out (FR-TM05); filtered on the server.
export type TaskTypeFilter = "firmware_update" | "resource_update";

interface ListTasksParams {
  limit?: number;
  cursor?: string;
  status?: string;
  taskType?: TaskTypeFilter;
  tenantId?: string;
}

export function useTasks(params: ListTasksParams = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.status) qs.set("status", params.status);
  if (params.taskType) qs.set("taskType", params.taskType);
  const query = qs.toString();

  return useQuery({
    queryKey: ["tasks", params.tenantId ?? null, params],
    queryFn: () => apiFetch<ListResponse<Task>>(`/api/v1/tasks${query ? `?${query}` : ""}`, {
      headers: params.tenantId ? { "X-Acting-Tenant-ID": params.tenantId } : undefined,
    }),
    placeholderData: keepPreviousData,
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: ["task", id],
    queryFn: () => apiFetch<SingleResponse<Task>>(`/api/v1/tasks/${id}`),
    enabled: !!id,
    refetchInterval: 5_000,
  });
}

// How many tasks match `status` across every page — kept apart from the
// page query so paging doesn't recount.
export function useTaskTotal(params: { status?: string; taskType?: TaskTypeFilter; tenantId?: string } = {}) {
  const qs = new URLSearchParams({ countOnly: "true" });
  if (params.status) qs.set("status", params.status);
  if (params.taskType) qs.set("taskType", params.taskType);

  return useQuery({
    queryKey: ["tasks", params.tenantId ?? null, "total", params.status ?? "", params.taskType ?? ""],
    queryFn: async () => {
      const res = await apiFetch<ListResponse<Task>>(`/api/v1/tasks?${qs.toString()}`, {
        headers: params.tenantId ? { "X-Acting-Tenant-ID": params.tenantId } : undefined,
      });
      return { total: res.pagination.total ?? 0, isLowerBound: res.pagination.totalIsLowerBound ?? false };
    },
    placeholderData: keepPreviousData,
  });
}

// Every task, loaded a page at a time on demand — for pickers. Tasks are
// unbounded, so the picker shows what's loaded plus a "Load more" rather
// than fetching the whole history up front.
export function useTaskPicker(params: { tenantId?: string; enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: ["tasks", params.tenantId ?? null, "picker"],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: "100" });
      if (pageParam) qs.set("cursor", pageParam);
      return apiFetch<ListResponse<Task>>(`/api/v1/tasks?${qs.toString()}`, {
        headers: params.tenantId ? { "X-Acting-Tenant-ID": params.tenantId } : undefined,
      });
    },
    initialPageParam: "",
    getNextPageParam: (last) => (last.pagination.hasMore ? last.pagination.cursor : undefined),
    enabled: params.enabled ?? true,
  });
}

// Server-side filters for a task's per-device progress. They apply across
// every page — never filter a page of this list in the browser.
export interface TaskDeviceFilters {
  status?: TaskDeviceStatus | "";
  search?: string;
}

// Task statuses during which device progress moves on its own.
const LIVE_TASK_STATUSES = new Set(["queued", "in_progress"]);
export const TASK_DEVICE_POLL_MS = 5_000;

// useTaskDeviceRefresh returns the poll interval for a task's device
// progress: on while the rollout is live, off otherwise. When the task
// stops being live it refetches the device rows once more, so the last
// terminal writes (or a cancel's failed rows) appear without a reload.
export function useTaskDeviceRefresh(taskId: string, taskStatus?: string): number | undefined {
  const qc = useQueryClient();
  const live = !!taskStatus && LIVE_TASK_STATUSES.has(taskStatus);
  const wasLive = useRef(live);

  useEffect(() => {
    if (wasLive.current && !live) {
      qc.invalidateQueries({ queryKey: ["task-devices", taskId] });
    }
    wasLive.current = live;
  }, [live, qc, taskId]);

  return live ? TASK_DEVICE_POLL_MS : undefined;
}

function taskDeviceFilterParams(filters: TaskDeviceFilters = {}): URLSearchParams {
  const qs = new URLSearchParams();
  if (filters.status) qs.set("status", filters.status);
  const search = filters.search?.trim();
  if (search) qs.set("search", search);
  return qs;
}

export function useTaskDevices(
  taskId: string,
  params: { limit?: number; cursor?: string; filters?: TaskDeviceFilters; refetchInterval?: number } = {},
) {
  const qs = taskDeviceFilterParams(params.filters);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString();

  return useQuery({
    queryKey: ["task-devices", taskId, "page", query],
    queryFn: () =>
      apiFetch<ListResponse<TaskDevice>>(
        `/api/v1/tasks/${taskId}/devices${query ? `?${query}` : ""}`,
      ),
    enabled: !!taskId,
    placeholderData: keepPreviousData,
    refetchInterval: params.refetchInterval,
  });
}

// How many of a task's devices match `filters` across every page.
export function useTaskDeviceTotal(
  taskId: string,
  params: { filters?: TaskDeviceFilters; refetchInterval?: number; enabled?: boolean } = {},
) {
  const qs = taskDeviceFilterParams(params.filters);
  const filterKey = qs.toString();
  qs.set("countOnly", "true");

  return useQuery({
    queryKey: ["task-devices", taskId, "total", filterKey],
    queryFn: async () => {
      const res = await apiFetch<ListResponse<TaskDevice>>(`/api/v1/tasks/${taskId}/devices?${qs.toString()}`);
      return { total: res.pagination.total ?? 0, isLowerBound: res.pagination.totalIsLowerBound ?? false };
    },
    enabled: !!taskId && (params.enabled ?? true),
    placeholderData: keepPreviousData,
    refetchInterval: params.refetchInterval,
  });
}

export function useCreateTask(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      taskType?: "firmware_update" | "resource_update";
      firmwareId?: string;
      resourceBundleId?: string;
      resourceBundleVersion?: number;
      targetType: string;
      targetIds?: string[];
      rolloutStrategy: string;
      stagedConfig?: StagedConfig;
      scheduledAt?: string;
      description?: string;
    }) =>
      apiFetch<SingleResponse<Task>>("/api/v1/tasks", {
        method: "POST",
        headers: tenantId ? { "X-Acting-Tenant-ID": tenantId } : undefined,
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function usePatchTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      name,
      description,
      rolloutStrategy,
      scheduledAt,
      stagedConfig,
    }: {
      id: string;
      action?: string;
      name?: string;
      description?: string;
      rolloutStrategy?: string;
      scheduledAt?: string;
      stagedConfig?: StagedConfig;
    }) =>
      apiFetch<SingleResponse<Task>>(`/api/v1/tasks/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...(action ? { action } : {}),
          ...(name !== undefined ? { name } : {}),
          ...(description !== undefined ? { description } : {}),
          ...(rolloutStrategy !== undefined ? { rolloutStrategy } : {}),
          ...(scheduledAt !== undefined ? { scheduledAt } : {}),
          ...(stagedConfig !== undefined ? { stagedConfig } : {}),
        }),
      }),
    onSuccess: (response) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["task", response.data.taskId] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/tasks/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (_response, id) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.removeQueries({ queryKey: ["task", id] });
      qc.removeQueries({ queryKey: ["task-devices", id] });
    },
  });
}
